import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Search } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { ACTION_OPTIONS, DELIVERY_OPTIONS, invoiceAnswered, nameKey } from "@/data/invoice";

// The Company Details page's third section (owner, 2026-10-11). Checkboxes, only one ticked per question:
//   1. charge the client and create an invoice?  Yes / No (No: nothing more)
//   2. draft and save / draft and approve / draft, approve and send
//   3. (send) email only / WhatsApp only / email and WhatsApp
// Yes: Xero is checked for this user straight away (connected for invoices, the item, their COMMISSION
// tracking option), so a problem shows before the affidavit is signed. From Xero the invoice is made out to
// the organisation loaded above; typed in by hand, Find Customer in Xero looks the client up by its
// Enterprise Name (exact, without (Pty) Ltd, shorter) - the user confirms or chooses, or it's done by hand.

const Tick = ({ checked, label, onChange, testId }) => (
  <label className={`bee-inv-tick${checked ? " is-checked" : ""}`}>
    <input type="checkbox" checked={checked} onChange={onChange} aria-label={label} data-testid={testId} />
    <span>{label}</span>
  </label>
);

const Question = ({ label, options, value, onChange, name }) => (
  <fieldset className="bee-inv-q" data-testid={`invoice-q-${name}`}>
    <legend className="bee-cd-label">
      {label}
      <span className="bee-cd-required" aria-hidden="true"> *</span>
    </legend>
    <div className="bee-inv-ticks">
      {options.map((o) => (
        <Tick
          key={String(o.value)}
          checked={value === o.value}
          // Only one ticked: ticking another unticks this one; unticking leaves the question open
          onChange={() => onChange(value === o.value ? null : o.value)}
          label={o.label}
          testId={`tick-${name}-${o.value}`}
        />
      ))}
    </div>
  </fieldset>
);

const CustomerModal = ({ matches, onPick, onNone, onCancel }) => (
  <div className="modal-overlay" data-testid="customer-dialog" onClick={onCancel}>
    <div className="modal-content bee-comm-modal" onClick={(e) => e.stopPropagation()}>
      <h2>Choose the Customer</h2>
      <p className="modal-description">These contacts in TC Auditors' Xero match the Enterprise Name. Click the one to invoice.</p>
      <div className="bee-comm-options">
        {matches.map((m) => (
          <button key={m.id} type="button" className="bee-comm-option" onClick={() => onPick(m)} data-testid={`customer-${m.id}`}>
            <span className="bee-comm-option-name">{m.name}</span>
            {m.email && <span className="bee-comm-option-title">{m.email}</span>}
          </button>
        ))}
      </div>
      <div className="modal-actions">
        <button type="button" className="bee-btn-plain" onClick={onNone} data-testid="button-customer-none">None of these</button>
        <button type="button" className="bee-btn-plain" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

const MANUAL = "No invoice will be created automatically: please create the invoice in Xero manually.";

// onUpdate(fn): fn gets the latest invoice answers and returns the new ones
const InvoiceDetails = ({ method, enterpriseName, xeroContact, invoice, onUpdate }) => {
  const { expired } = useAuth();
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState("");
  const [confirm, setConfirm] = useState(null); // one contact found by a shorter name: is it the right one?
  const [choices, setChoices] = useState(null); // several found: choose one
  const set = (changes) => onUpdate((inv) => ({ ...inv, ...changes }));
  const nameNow = nameKey(enterpriseName);
  const forThisName = invoice.forName === nameNow;

  // Yes ticked: can this user's invoice be created in Xero?
  const needsCheck = invoice.charge === true && !invoice.ready;
  useEffect(() => {
    if (!needsCheck) return undefined;
    let live = true;
    const done = (ready) => live && onUpdate((inv) => (inv.charge === true ? { ...inv, ready } : inv));
    api("/xero/invoice/ready")
      .then(done)
      .catch((err) => (err.status === 401 ? expired() : done({ ready: false, message: err.message })));
    return () => { live = false; };
  }, [needsCheck, onUpdate, expired]);

  const found = (customer) => {
    setConfirm(null);
    setChoices(null);
    set({ customer, gaveUp: "", forName: nameNow });
  };
  const giveUp = (why) => {
    setConfirm(null);
    setChoices(null);
    set({ customer: null, gaveUp: why ? `${why} ${MANUAL}` : MANUAL, forName: nameNow });
  };

  const findCustomer = async () => {
    setFinding(true);
    setFindError("");
    setConfirm(null);
    set({ customer: null, gaveUp: "", forName: "" });
    try {
      const { how, matches } = await api(`/xero/customers?${new URLSearchParams({ name: enterpriseName })}`);
      if (matches.length === 0) giveUp(`"${enterpriseName.trim()}" wasn't found in Xero.`);
      else if (matches.length > 1) setChoices(matches);
      else if (how === "exact") found(matches[0]);
      else setConfirm(matches[0]);
    } catch (err) {
      if (err.status === 401) expired();
      else setFindError(err.message);
    } finally {
      setFinding(false);
    }
  };

  const answered = invoiceAnswered(invoice);
  const checking = invoice.charge === true && !invoice.ready;

  return (
    <section className="bee-cd-section" aria-labelledby="invoice-title" data-testid="section-invoice">
      <h2 id="invoice-title" className="bee-cd-title">Invoice Details</h2>
      <div className="bee-cd-fields">
        <Question
          name="charge"
          label="Charge the client for this B-BBEE affidavit and create an invoice in Xero?"
          options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]}
          value={invoice.charge}
          // A new answer: the questions after it start again (Yes: Xero is checked again)
          onChange={(charge) => { setConfirm(null); set({ charge, action: "", delivery: "", ready: null }); }}
        />

        {invoice.charge === true && checking && (
          <p className="bee-field-hint bee-inv-checking" data-testid="invoice-checking">
            <Loader2 size={14} className="bee-spin" /> Checking TC Auditors' Xero for invoices...
          </p>
        )}
        {invoice.charge === true && invoice.ready && !invoice.ready.ready && (
          <div className="bee-inv-problem" data-testid="invoice-not-ready">
            <p className="bee-field-hint is-error" role="alert">
              {invoice.ready.message} Tick <strong>No</strong> to continue without an invoice.
            </p>
            <button type="button" className="bee-btn-small" onClick={() => set({ ready: null })} data-testid="button-invoice-recheck">
              <RefreshCw size={14} /> <span>Check again</span>
            </button>
          </div>
        )}

        {invoice.charge === true && invoice.ready?.ready && (
          <Question
            name="action"
            label="Should the invoice be:"
            options={ACTION_OPTIONS}
            value={invoice.action}
            onChange={(action) => set({ action: action || "", delivery: action === "send" ? invoice.delivery : "" })}
          />
        )}
        {invoice.charge === true && invoice.ready?.ready && invoice.action === "send" && (
          <Question
            name="delivery"
            label="How should the invoice be sent?"
            options={DELIVERY_OPTIONS}
            value={invoice.delivery}
            onChange={(delivery) => set({ delivery: delivery || "" })}
          />
        )}

        {invoice.charge === true && answered && method === "xero" && (
          xeroContact ? (
            <p className="bee-cd-ok" data-testid="invoice-to">
              {/* Owner, 2026-10-11: shows once the last question is answered (draft / approve, or how to send) */}
              <CheckCircle2 size={15} /> The invoice will be made out to {xeroContact.name} on the next screen.
            </p>
          ) : (
            <p className="bee-field-hint" data-testid="invoice-to">
              The invoice will be made out to the organisation you load from Xero above, on the next screen.
            </p>
          )
        )}

        {invoice.charge === true && answered && method === "manual" && (
          <div className="bee-cd-field">
            <button
              type="button"
              className="bee-btn-green"
              onClick={findCustomer}
              disabled={finding || !nameNow}
              title={nameNow ? "Find the client's contact in TC Auditors' Xero" : "Type the Enterprise Name first"}
              data-testid="button-find-customer"
            >
              {finding ? <Loader2 size={16} className="bee-spin" /> : <Search size={16} />} <span>Find Customer in Xero</span>
            </button>
            {findError && <p className="bee-field-hint is-error" role="alert" data-testid="customer-error">{findError}</p>}
            {confirm && (
              <Question
                name="confirm"
                label={`Is "${confirm.name}" the correct Xero contact?`}
                options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]}
                value={null}
                onChange={(yes) => (yes ? found(confirm) : giveUp(""))}
              />
            )}
            {forThisName && invoice.customer && (
              <p className="bee-cd-ok" data-testid="customer-found">
                <CheckCircle2 size={15} /> Found in Xero: {invoice.customer.name}. The invoice will be made out to this contact on the next screen.
              </p>
            )}
            {forThisName && invoice.gaveUp && (
              <p className="bee-inv-manual" role="status" data-testid="customer-manual">
                <AlertTriangle size={15} /> {invoice.gaveUp}
              </p>
            )}
          </div>
        )}
      </div>

      {choices && (
        <CustomerModal matches={choices} onPick={found} onNone={() => giveUp("")} onCancel={() => setChoices(null)} />
      )}
    </section>
  );
};

export default InvoiceDetails;
